import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Nullable } from "primereact/ts-helpers";
import { useEffect, useState } from "react";
import { createLotteryService } from "../services/adminLottery.service";
import { CreateLotteryForm } from "../model/adminLottery.model";
import { ToastControlBackendModel } from "../../../../../components/Sidebar/ToastBackControl/models/toastBackControl.model";

interface Props {
  getAllLotteryData: () => void;
  setMessageBackend: (value: ToastControlBackendModel) => void;
}

function FormCreateLottery({
  getAllLotteryData,
  setMessageBackend,
}: Props): JSX.Element {
  const [descriptionLottery, setDescriptionLottery] = useState<string>("");
  const [rewardLottery, setRewardLottery] = useState<number>(0);
  const [dateEndLottery, setDateEndLottery] = useState<Nullable<Date>>(null);
  const [validateForm, setValidateForm] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidateForm(false);
    try {
      const data: CreateLotteryForm = {
        descriptionLottery,
        rewardLottery,
        dateEndLottery: dateEndLottery
          ? dateEndLottery.toISOString().split("T")[0]
          : null,
      };
      const response = await createLotteryService(data);
      if (response.statusCode < 299) {
        getAllLotteryData();
        setMessageBackend({
          detailValue: "Sorteo creado con éxito",
          severityValue: "success",
          lifeValue: 3000,
          validateShowMessage: true,
        });
        setDescriptionLottery("");
        setRewardLottery(0);
        setDateEndLottery(null);
      }
    } catch (error) {
      setMessageBackend({
        detailValue: "Error al crear el sorteo",
        severityValue: "error",
        lifeValue: 3000,
        validateShowMessage: true,
      });
    } finally {
      setValidateForm(true);
    }
  };

  useEffect(() => {
    if (
      descriptionLottery.trim() !== "" &&
      rewardLottery > 0 &&
      dateEndLottery !== null
    ) {
      setValidateForm(true);
    } else {
      setValidateForm(false);
    }
  }, [descriptionLottery, rewardLottery, dateEndLottery]);

  const inputStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    color: "#333333",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "0.75rem 1rem",
    width: "100%",
  };

  return (
    <div className="w-full py-2">
      <style>{`
        /* Placeholders */
        .p-inputtext::placeholder {
          color: #64748b !important;
          opacity: 1 !important;
        }

        /* Focus inputs */
        .p-inputtext:focus {
          outline: none !important;
          border-color: #00A86B !important;
          box-shadow: 0 0 0 2px rgba(0, 168, 107, 0.15) !important;
        }

        /* Botón icono calendario */
        .p-calendar .p-datepicker-trigger {
          background: #f1f5f9 !important;
          border: 1px solid #cbd5e1 !important;
          border-left: none !important;
          color: #64748b !important;
        }
        .p-calendar .p-datepicker-trigger:hover {
          background: #e2e8f0 !important;
          color: #374151 !important;
        }

        /* ── Panel del calendario ── */
        .p-datepicker {
          background: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          color: #374151 !important;
          box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important;
        }

        /* Header (mes/año + flechas) */
        .p-datepicker .p-datepicker-header {
          background: #ffffff !important;
          color: #374151 !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 0.6rem 1rem !important;
        }
        .p-datepicker .p-datepicker-header .p-datepicker-title {
          color: #374151 !important;
          font-weight: 600 !important;
        }
        .p-datepicker .p-datepicker-header button {
          color: #6b7280 !important;
          background: transparent !important;
          border: none !important;
        }
        .p-datepicker .p-datepicker-header button:hover {
          background: #f1f5f9 !important;
          color: #374151 !important;
          border-radius: 6px !important;
        }

        /* Días de la semana */
        .p-datepicker table th > span {
          color: #9ca3af !important;
          font-size: 0.78rem !important;
          font-weight: 600 !important;
        }

        /* Días del mes */
        .p-datepicker table td > span {
          color: #374151 !important;
          border-radius: 6px !important;
        }
        .p-datepicker table td > span:hover {
          background: #f0fdf9 !important;
          color: #00A86B !important;
        }

        /* Día seleccionado */
        .p-datepicker table td > span.p-highlight {
          background: #00A86B !important;
          color: #ffffff !important;
        }

        /* Hoy */
        .p-datepicker table td.p-datepicker-today > span {
          background: #e6f7f1 !important;
          color: #00A86B !important;
          font-weight: 700 !important;
        }

        /* Días fuera del mes */
        .p-datepicker table td.p-datepicker-other-month > span {
          color: #d1d5db !important;
        }
      `}</style>

      <form
        className="flex flex-row flex-wrap md:flex-nowrap align-items-center gap-3 w-full"
        onSubmit={onSubmit}
      >
        {/* Descripción */}
        <div className="w-full">
          <InputText
            autoFocus
            style={inputStyle}
            type="text"
            placeholder="Descripción del sorteo"
            id="descriptionLottery"
            value={descriptionLottery}
            onChange={(e) => setDescriptionLottery(e.target.value)}
          />
        </div>

        {/* Ganancia */}
        <div className="w-full">
          <InputText
            style={inputStyle}
            type="number"
            maxLength={10}
            id="rewardLottery"
            placeholder="Ganancia"
            value={rewardLottery === 0 ? "" : rewardLottery.toString()}
            onChange={(e) => setRewardLottery(Number(e.target.value))}
          />
        </div>

        {/* Calendario */}
        <div className="w-full">
          <Calendar
            id="dateEndLottery"
            value={dateEndLottery}
            onChange={(e) => setDateEndLottery(e.value)}
            dateFormat="yy/mm/dd"
            placeholder="Fecha de sorteo"
            className="w-full"
            inputStyle={inputStyle}
            style={{ width: "100%" }}
            showIcon
          />
        </div>

        {/* Botón Crear */}
        <div className="w-full md:w-auto">
          <Button
            type="submit"
            label="Crear"
            className="text-white font-semibold border-round px-5"
            style={{
              backgroundColor: "#00A86B",
              borderColor: "#00A86B",
              paddingTop: "0.75rem",
              paddingBottom: "0.75rem",
              minWidth: "140px",
            }}
            disabled={!validateForm}
          />
        </div>
      </form>
    </div>
  );
}

export default FormCreateLottery;